/**
 * SessionManager — mints a `session_id` (uuid) that rotates after 30 minutes of
 * inactivity. Persisted via @lib/storage so it survives a JS reload inside the
 * window. In-memory state keeps `getSessionId()` synchronous (needed at enqueue
 * time); persistence is opportunistic.
 *
 * Note: tl-app's @lib/storage is value-in/value-out (getData/setData handle
 * their own JSON wrapping), so we pass/read plain objects — no manual
 * JSON.parse/stringify here.
 */
import uuid from "react-native-uuid";
import { getData, setData } from "@lib/storage";

const SESSION_KEY = "wisetap_session";
const IDLE_MS = 30 * 60 * 1000;

let sessionId: string | null = null;
let lastActive = 0;

const persist = (): void => {
  setData(SESSION_KEY, { sessionId, lastActive });
};

const rotate = (): string => {
  sessionId = String(uuid.v4());
  lastActive = Date.now();
  persist();
  return sessionId;
};

/** Rehydrate a still-valid session from storage, or start a fresh one. */
export const hydrateSession = async (): Promise<string> => {
  try {
    const parsed = await getData(SESSION_KEY);
    if (parsed?.sessionId && Date.now() - (parsed.lastActive || 0) < IDLE_MS) {
      sessionId = parsed.sessionId;
      lastActive = parsed.lastActive;
      return sessionId as string;
    }
  } catch {
    // fall through to a fresh session
  }
  return rotate();
};

/** Current session id, rotating if idle beyond the window. Synchronous. */
export const getSessionId = (): string => {
  const now = Date.now();
  if (!sessionId || now - lastActive >= IDLE_MS) return rotate();
  lastActive = now; // extend the window; persisted lazily via touchSession()
  return sessionId;
};

/** Mark activity + persist (call on app foreground / periodic flush). */
export const touchSession = (): void => {
  lastActive = Date.now();
  persist();
};

/** Force a new session (call on logout). */
export const resetSession = (): string => rotate();
